import { useState, useEffect, useCallback } from 'react';
// @mui
import Card from '@mui/material/Card';
import Table from '@mui/material/Table';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
// components
import Iconify from 'src/components/iconify';
import Scrollbar from 'src/components/scrollbar';
import { useSettingsContext } from 'src/components/settings';
import { useSnackbar } from 'src/components/snackbar';
import CustomBreadcrumbs from 'src/components/custom-breadcrumbs';
// routes
import { paths } from 'src/routes/paths';
// utils
import axios, { API_ENDPOINTS } from 'src/utils/axios';
// sections
import UserFormDialog from '../user-form-dialog';
import UserPasswordDialog from '../user-password-dialog';

// ----------------------------------------------------------------------

export default function UserListView() {
  const settings = useSettingsContext();
  const { enqueueSnackbar } = useSnackbar();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.users.list);
      setUsers(response.data);
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Erro ao carregar utilizadores', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenCreate = () => {
    setSelectedUser(null);
    setFormOpen(true);
  };

  const handleOpenEdit = (user) => {
    setSelectedUser(user);
    setFormOpen(true);
  };

  const handleOpenPassword = (user) => {
    setSelectedUser(user);
    setPasswordOpen(true);
  };

  const handleOpenDelete = (user) => {
    setSelectedUser(user);
    setDeleteOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setSelectedUser(null);
  };

  const handleClosePassword = () => {
    setPasswordOpen(false);
    setSelectedUser(null);
  };

  const handleCloseDelete = () => {
    setDeleteOpen(false);
    setSelectedUser(null);
  };

  const handleToggleStatus = async (user) => {
    try {
      await axios.put(`${API_ENDPOINTS.users.status}${user.id}/status?isActive=${!user.isActive}`);
      enqueueSnackbar(user.isActive ? 'Utilizador desativado' : 'Utilizador ativado', { variant: 'success' });
      fetchUsers();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error.message || 'Erro ao alterar estado', { variant: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_ENDPOINTS.users.delete}${selectedUser.id}`);
      enqueueSnackbar('Utilizador eliminado', { variant: 'success' });
      handleCloseDelete();
      fetchUsers();
    } catch (error) {
      console.error(error);
      enqueueSnackbar(error.message || 'Erro ao eliminar utilizador', { variant: 'error' });
    }
  };

  const handleFormSuccess = () => {
    handleCloseForm();
    fetchUsers();
  };

  const handlePasswordSuccess = () => {
    handleClosePassword();
  };

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <CustomBreadcrumbs
        heading="Utilizadores"
        links={[
          { name: 'Dashboard', href: paths.dashboard.root },
          { name: 'Utilizadores' },
        ]}
        action={
          <Button
            variant="contained"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={handleOpenCreate}
          >
            Novo Utilizador
          </Button>
        }
        sx={{ mb: { xs: 3, md: 5 } }}
      />

      <Card>
        <Scrollbar>
          <TableContainer sx={{ minWidth: 800 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="subtitle2">
                          {user.firstname} {user.lastname}
                        </Typography>
                        {user.isProtected && (
                          <Tooltip title="Conta protegida - não pode ser eliminada">
                            <Iconify icon="mdi:shield-check" color="primary.main" />
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role}
                        size="small"
                        color={user.role === 'ADMINISTRATOR' ? 'primary' : 'default'}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isActive ? 'Ativo' : 'Inativo'}
                        size="small"
                        color={user.isActive ? 'success' : 'error'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" justifyContent="flex-end" spacing={0.5}>
                        <Tooltip title="Editar">
                          <IconButton onClick={() => handleOpenEdit(user)} size="small">
                            <Iconify icon="solar:pen-bold" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Alterar Password">
                          <IconButton onClick={() => handleOpenPassword(user)} size="small">
                            <Iconify icon="mdi:key" />
                          </IconButton>
                        </Tooltip>

                        {!user.isProtected && (
                          <>
                            <Tooltip title={user.isActive ? 'Desativar' : 'Ativar'}>
                              <IconButton
                                onClick={() => handleToggleStatus(user)}
                                size="small"
                                color={user.isActive ? 'warning' : 'success'}
                              >
                                <Iconify icon={user.isActive ? 'mdi:account-off' : 'mdi:account-check'} />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Eliminar">
                              <IconButton
                                onClick={() => handleOpenDelete(user)}
                                size="small"
                                color="error"
                              >
                                <Iconify icon="solar:trash-bin-trash-bold" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

                {!loading && users.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                        Nenhum utilizador encontrado
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Scrollbar>
      </Card>

      {/* Form Dialog */}
      <UserFormDialog
        open={formOpen}
        onClose={handleCloseForm}
        user={selectedUser}
        onSuccess={handleFormSuccess}
      />

      {/* Password Dialog */}
      <UserPasswordDialog
        open={passwordOpen}
        onClose={handleClosePassword}
        user={selectedUser}
        onSuccess={handlePasswordSuccess}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteOpen} onClose={handleCloseDelete}>
        <DialogTitle>Eliminar Utilizador</DialogTitle>
        <DialogContent>
          <Typography>
            Tem a certeza que deseja eliminar o utilizador{' '}
            <strong>{selectedUser?.firstname} {selectedUser?.lastname}</strong>?
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            Esta ação não pode ser revertida.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDelete}>Cancelar</Button>
          <Button onClick={handleDelete} variant="contained" color="error">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
